/**
 * IP地理位置和时区匹配检测器
 * 用于检测用户的IP地理位置和系统时区是否匹配
 */

export default class GeoLocationMatcher {
  constructor() {
    this.systemTimezone = null;
    this.expectedTimezone = null;
    this.countryCode = null;
    this.isMatching = null;
    this.confidence = 0;
    // 国家与时区的映射关系
    this.countryTimezones = this.getCountryTimezones();
  }

  /**
   * 检测IP地理位置和时区是否匹配
   * @param {Object} ipInfo IP信息对象
   * @returns {Object} 检测结果
   */
  checkMatch(ipInfo) {
    if (!ipInfo || !ipInfo.country) {
      return {
        isMatching: null,
        confidence: 0,
        systemTimezone: this.getSystemTimezone(),
        expectedTimezone: null,
        message: '无法检测: 缺少IP地理位置信息'
      };
    }

    // 获取系统时区
    this.systemTimezone = this.getSystemTimezone();
    
    // 获取国家代码
    this.countryCode = ipInfo.country.toUpperCase();
    
    // 根据IP位置估计预期时区
    this.expectedTimezone = this.getExpectedTimezone(ipInfo);
    
    // 比较两个时区是否匹配
    let result = this.compareTimezones();
    
    // 如果使用了VPN/代理/Tor，预期结果是不匹配（作为隐私保护的指标）
    if (ipInfo.usingVPN || ipInfo.usingProxy || ipInfo.usingTor) {
      // 如果时区匹配，这可能表示VPN配置不完整
      if (result.isMatching) {
        result.message = '检测到VPN/代理，但时区仍然匹配您的地理位置，这可能降低匿名性';
        result.recommendation = '考虑更改系统时区以增强隐私保护';
      } else {
        result.message = '检测到VPN/代理，且时区与IP地理位置不匹配，这有助于保护您的隐私';
        result.recommendation = null;
      }
    }
    
    return result;
  }

  /**
   * 获取系统时区
   * @returns {string} 系统时区
   */
  getSystemTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * 根据IP信息预测预期时区
   * @param {Object} ipInfo IP信息对象
   * @returns {string|null} 预期时区
   */
  getExpectedTimezone(ipInfo) {
    // 首先使用IP信息中可能提供的时区
    if (ipInfo.timezone) {
      return ipInfo.timezone;
    }
    
    // 或者根据国家代码查找最可能的时区
    const countryCode = this.countryCode;
    if (countryCode && this.countryTimezones[countryCode]) {
      // 如果国家有多个时区，简单地返回第一个(主要时区)
      return this.countryTimezones[countryCode][0];
    }
    
    return null;
  }

  /**
   * 比较系统时区和预期时区
   * @returns {Object} 比较结果
   */
  compareTimezones() {
    if (!this.systemTimezone || !this.expectedTimezone) {
      return {
        isMatching: null,
        confidence: 0,
        systemTimezone: this.systemTimezone,
        expectedTimezone: this.expectedTimezone,
        message: '无法完成比较: 缺少时区信息'
      };
    }
    
    // 判断时区是否匹配
    // 1. 精确匹配
    const exactMatch = this.systemTimezone === this.expectedTimezone;
    
    // 2. 时区组匹配 (同一国家的不同时区)
    const zoneGroupMatch = this.areInSameZoneGroup();
    
    // 3. 偏移量匹配 (不同名称但相同的UTC偏移)
    const offsetMatch = this.haveMatchingOffset();
    
    // 确定匹配结果和置信度
    let isMatching, confidence, message;
    
    if (exactMatch) {
      isMatching = true;
      confidence = 1.0;
      message = '系统时区与您的IP地理位置精确匹配';
    } else if (zoneGroupMatch) {
      isMatching = true;
      confidence = 0.8;
      message = '系统时区与您的IP地理位置所在国家的时区匹配';
    } else if (offsetMatch) {
      isMatching = true;
      confidence = 0.7;
      message = '系统时区与您的IP地理位置在同一时间偏移量内';
    } else {
      isMatching = false;
      confidence = 0.9;
      message = '系统时区与您的IP地理位置不匹配';
    }
    
    // 保存结果
    this.isMatching = isMatching;
    this.confidence = confidence;
    
    return {
      isMatching,
      confidence,
      systemTimezone: this.systemTimezone,
      expectedTimezone: this.expectedTimezone,
      message,
      recommendation: isMatching ? null : '如果您正在使用VPN，这是一个良好的隐私迹象'
    };
  }

  /**
   * 检查两个时区是否在同一区域组
   * @returns {boolean} 是否在同一区域组
   */
  areInSameZoneGroup() {
    if (!this.systemTimezone || !this.expectedTimezone) {
      return false;
    }
    
    // 简单检查时区前缀是否相同
    // 例如: "America/New_York" 和 "America/Chicago" 都是美国时区
    const systemPrefix = this.systemTimezone.split('/')[0];
    const expectedPrefix = this.expectedTimezone.split('/')[0];
    
    return systemPrefix === expectedPrefix;
  }

  /**
   * 检查两个时区的时间偏移是否匹配
   * @returns {boolean} 是否有相同的偏移量
   */
  haveMatchingOffset() {
    if (!this.systemTimezone || !this.expectedTimezone) {
      return false;
    }
    
    try {
      // 获取当前的UTC偏移量
      const now = new Date();
      
      // 系统时区的偏移量
      const systemOffset = this.getTimezoneOffset(this.systemTimezone, now);
      
      // 预期时区的偏移量
      const expectedOffset = this.getTimezoneOffset(this.expectedTimezone, now);
      
      // 比较偏移量是否相同
      return systemOffset === expectedOffset;
    } catch (e) {
      return false;
    }
  }

  /**
   * 获取指定时区在指定日期的UTC偏移量(分钟)
   * @param {string} timezone 时区
   * @param {Date} date 日期
   * @returns {number} UTC偏移量(分钟)
   */
  getTimezoneOffset(timezone, date) {
    try {
      // 使用Intl API获取格式化的时间字符串
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeStyle: 'long',
        hour12: false
      });
      
      // 获取格式化的时间字符串，例如: "14:30:45 GMT+08:00"
      const formatted = formatter.format(date);
      
      // 提取GMT部分，例如: "GMT+08:00"
      const match = formatted.match(/GMT([+-]\d{2}):(\d{2})/);
      if (!match) return null;
      
      // 计算偏移分钟数
      const hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      
      return hours * 60 + Math.sign(hours) * minutes;
    } catch (e) {
      // 如果发生错误，回退到更简单的方法
      try {
        // 创建指定时区的日期
        const offsetDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
        // 计算与UTC的偏移量(分钟)
        return (date.getTime() - offsetDate.getTime()) / 60000;
      } catch (err) {
        return null;
      }
    }
  }

  /**
   * 获取主要国家时区映射
   * @returns {Object} 国家到时区的映射
   */
  getCountryTimezones() {
    // 部分国家的时区映射，可以根据需要扩展
    return {
      'US': ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles'],
      'CA': ['America/Toronto', 'America/Vancouver', 'America/Edmonton'],
      'GB': ['Europe/London'],
      'AU': ['Australia/Sydney', 'Australia/Melbourne', 'Australia/Perth'],
      'CN': ['Asia/Shanghai'],
      'JP': ['Asia/Tokyo'],
      'DE': ['Europe/Berlin'],
      'FR': ['Europe/Paris'],
      'BR': ['America/Sao_Paulo'],
      'IN': ['Asia/Kolkata'],
      'RU': ['Europe/Moscow', 'Asia/Yekaterinburg', 'Asia/Omsk', 'Asia/Krasnoyarsk', 'Asia/Irkutsk', 'Asia/Yakutsk', 'Asia/Vladivostok'],
      'ES': ['Europe/Madrid'],
      'IT': ['Europe/Rome'],
      'NL': ['Europe/Amsterdam'],
      'SG': ['Asia/Singapore'],
      'SE': ['Europe/Stockholm'],
      'CH': ['Europe/Zurich'],
      'NO': ['Europe/Oslo'],
      'DK': ['Europe/Copenhagen'],
      'FI': ['Europe/Helsinki'],
      'NZ': ['Pacific/Auckland'],
      'ZA': ['Africa/Johannesburg'],
      'MX': ['America/Mexico_City'],
      'AR': ['America/Argentina/Buenos_Aires'],
      'TH': ['Asia/Bangkok'],
      'MY': ['Asia/Kuala_Lumpur'],
      'ID': ['Asia/Jakarta']
    };
  }
} 